import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Procedure, Patient, Encounter } from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import {
  prepareProcedureForSave,
  setStatus,
  setEndDate,
} from '@/utils/procedure.utils';

/**
 * Return type interface for the useTherapeuticServiceActions hook.
 * Centralizes all therapeutic service server operations.
 */
export interface UseTherapeuticServiceActionsReturn {
  /**
   * Save a therapeutic service procedure (create or update).
   * Returns the saved procedure with server-assigned ID.
   *
   * @param procedure - The procedure to save
   * @returns Promise resolving to the saved procedure
   */
  saveProcedure: (procedure: Procedure) => Promise<Procedure>;

  /**
   * Stop a therapeutic service with an end date and optional reason.
   * Saves immediately to server.
   *
   * @param procedure - The procedure to stop
   * @param endDate - The end date for the service
   * @param reason - Optional reason for stopping
   * @returns Promise resolving to the stopped procedure
   */
  stopProcedure: (
    procedure: Procedure,
    endDate: Date,
    reason?: string
  ) => Promise<Procedure>;

  /**
   * Soft delete a therapeutic service procedure.
   * Marks the status as 'entered-in-error' to preserve audit trail.
   * Matches medication deletion pattern.
   *
   * @param id - The ID of the procedure to soft delete
   * @returns Promise that resolves when soft deletion is complete
   */
  deleteProcedure: (id: string) => Promise<void>;
}

/**
 * Custom hook for therapeutic service server operations.
 * Provides functions to save and delete therapeutic service procedures.
 *
 * Pattern matches useMedicationActions for consistency.
 *
 * @param patient - The patient the services belong to
 * @param encounter - The current encounter
 * @returns Object with saveProcedure and deleteProcedure functions
 *
 * @example
 * const { saveProcedure, deleteProcedure } = useTherapeuticServiceActions(patient, encounter);
 *
 * // Save a new service
 * const saved = await saveProcedure(newProcedure);
 *
 * // Delete a service
 * await deleteProcedure(procedureId);
 */
export function useTherapeuticServiceActions(
  patient: Patient,
  encounter: Encounter
): UseTherapeuticServiceActionsReturn {
  const medplum = useMedplum();

  /**
   * Save a therapeutic service procedure to the server.
   * Handles both creation and updates automatically.
   */
  const saveProcedure = useCallback(
    async (procedure: Procedure): Promise<Procedure> => {
      // 1. Prepare procedure using utility (validates and cleans)
      let preparedProcedure = prepareProcedureForSave(procedure);

      // 2. Set patient and encounter references if missing
      if (!preparedProcedure.subject) {
        preparedProcedure = {
          ...preparedProcedure,
          subject: createReference(patient),
        };
      }
      if (!preparedProcedure.encounter) {
        preparedProcedure = {
          ...preparedProcedure,
          encounter: createReference(encounter),
        };
      }

      // 3. Create or update based on presence of ID
      let saved: Procedure;
      if (preparedProcedure.id) {
        saved = await medplum.updateResource(preparedProcedure);
      } else {
        saved = await medplum.createResource(preparedProcedure);
      }

      // 4. Invalidate cache to ensure UI reflects changes immediately
      medplum.invalidateSearches('Procedure');

      return saved;
    },
    [medplum, patient, encounter]
  );

  /**
   * Stop a therapeutic service procedure.
   * Sets status to 'stopped' and updates the end date.
   */
  const stopProcedure = useCallback(
    async (
      procedure: Procedure,
      endDate: Date,
      reason?: string
    ): Promise<Procedure> => {
      // Build stopped procedure using utility functions
      let stoppedProc = setStatus(procedure, 'stopped');
      stoppedProc = setEndDate(stoppedProc, endDate);

      // Store reason in outcome.text if provided
      if (reason) {
        stoppedProc = {
          ...stoppedProc,
          outcome: { text: reason },
        };
      }

      // Use saveProcedure to persist
      return saveProcedure(stoppedProc);
    },
    [saveProcedure]
  );

  /**
   * Soft delete a therapeutic service procedure from the server.
   * Marks status as 'entered-in-error' to preserve audit trail.
   * Matches medication deletion pattern.
   */
  const deleteProcedure = useCallback(
    async (id: string): Promise<void> => {
      // 1. Read existing procedure
      const existing = await medplum.readResource('Procedure', id);

      // 2. Update status to entered-in-error (soft delete)
      await medplum.updateResource({
        ...existing,
        status: 'entered-in-error',
      });

      // 3. Invalidate cache to ensure UI reflects changes immediately
      medplum.invalidateSearches('Procedure');
    },
    [medplum]
  );

  return {
    saveProcedure,
    stopProcedure,
    deleteProcedure,
  };
}
